import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { contactPath } from '../seo/routes';
import { azure, ink, line } from '../theme';

// Shared chrome for the three service deep-dive panels: sticky header with
// close action, scrollable body, and a consistent contact CTA at the end,
// which opens the contact form with this practice (`topic`) preselected.
export default function DetailPanel({ open, onClose, title, eyebrow, topic, children }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  return (
    <Drawer
      anchor={isRTL ? 'left' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 'min(720px, 100vw)', bgcolor: ink.raised },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: { xs: 3, sm: 4 },
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: alpha(ink.raised, 0.92),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${line}`,
        }}
      >
        <Box>
          {eyebrow && (
            <Typography variant="overline" sx={{ color: azure[300] }}>
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h4" component="h2">
            {title}
          </Typography>
        </Box>
        <IconButton aria-label={t('panel.close')} onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </Stack>

      <Box sx={{ px: { xs: 3, sm: 4 }, py: 4 }}>
        {children}

        <Box
          sx={{
            mt: 5,
            p: 3,
            borderRadius: 4,
            border: `1px solid ${alpha(azure[400], 0.3)}`,
            background: `linear-gradient(135deg, ${alpha(azure[500], 0.12)}, ${alpha(
              '#10B981',
              0.08
            )})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h5" component="p" sx={{ maxWidth: 340 }}>
            {t('contact.title')}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to={contactPath(i18n.language, topic)}
            startIcon={<MailOutlineRounded />}
          >
            {t('panel.cta')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export function PanelSection({ title, children, sx }) {
  return (
    <Box sx={{ mt: 4, ...sx }}>
      {title && (
        <Typography variant="h5" component="h3" sx={{ mb: 1.5 }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export function CheckList({ items, dense = true }) {
  return (
    <List dense={dense} disablePadding>
      {items.map((item) => (
        <ListItem key={item} disableGutters sx={{ alignItems: 'flex-start', py: 0.6 }}>
          <ListItemIcon sx={{ mt: '3px' }}>
            <CheckCircleRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={item}
            primaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
          />
        </ListItem>
      ))}
    </List>
  );
}
